import { TFunction, useTranslation } from 'react-i18next';
import {
	EllipsisOutlined,
	LoadingOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import Breadcrumb from 'components/Breadcrumb';
import { getCellValue, getShowTotal } from 'utils/tableUtils';
import {
	Button,
	Table,
	Dropdown,
	Menu,
	Modal,
	TablePaginationConfig,
	Spin,
} from 'antd';
import { projectSelector } from 'redux/selectors/projectSelector';
import OmniNotification from 'components/OmniNotification';
import styles from './GameStatsPage.module.less';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api, {
	GameStatDto,
	GameStatDtoPagedResult,
	GameStatFormat,
	LeaderboardSortField,
	ListLeaderboardsDto,
	SortOrder,
	UnitOfMeasurement,
} from 'api';
import { useSelector } from 'react-redux';
import {
	TGameStatFormate,
	TGameStatUnitOfMeasurement,
} from 'utils/productUtils';
import { IRoutes } from 'components/Breadcrumb/Breadcrumb';

interface LeaderboardFetchResult {
	isLoading?: boolean;
	count?: number;
	items?: ListLeaderboardsDto[] | undefined;
}

type LeaderboardMap = Map<number, LeaderboardFetchResult>;

export default function StatsPage() {
	const { t } = useTranslation();
	const showTotal = useCallback(
		(total: number, range: [number, number]) => getShowTotal(total, range, t),
		[t],
	);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		size: 'default',
		showQuickJumper: true,
		showTotal,
		showSizeChanger: true,
		total: 0,
	});
	const [routes, setRoutes] = useState<IRoutes[]>([]);
	const history = useHistory();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const projectInfo = useSelector(projectSelector);
	const [listData, setListData] = useState<GameStatDtoPagedResult>();
	const [leaderboardMap, setLeaderboardMap] = useState<LeaderboardMap>();

	const setLeaderboard = (
		key: number,
		updatedProperties: Partial<LeaderboardFetchResult>,
	) => {
		setLeaderboardMap((prevMaps) => {
			prevMaps = prevMaps || new Map();
			const updatedMaps = new Map(prevMaps);
			updatedMaps.set(key, {
				...(prevMaps.get(key) || {}),
				...updatedProperties,
			});
			return updatedMaps;
		});
	};

	const getLeaderboard = (key: number) => {
		if (leaderboardMap) {
			return leaderboardMap.get(key);
		}
		return undefined;
	};

	const syncLeaderboard = async (record: GameStatDto) => {
		try {
			setLeaderboard(record.id!, { isLoading: true });
			const { items } = await api.getLeaderboardsList(
				projectInfo?.id!,
				SortOrder.ASC,
				LeaderboardSortField.ApiName,
				0,
				999,
			);
			const itemsOfRecord = items?.filter(
				(item) => item.gameStatId === record.id,
			);
			setLeaderboard(record.id!, {
				count: itemsOfRecord?.length,
				items: itemsOfRecord,
			});
		} finally {
			setLeaderboard(record.id!, { isLoading: false });
		}
	};

	function navigateToCreateStat() {
		history.push(`game-stats/create`);
	}

	function navigateToEditStat(gameStatId: string, apiName: string) {
		history.push(`game-stats/edit/${gameStatId}/${apiName}`);
	}

	const deleteStatsCallback = useCallback(
		(record) => {
			if (getLeaderboard(record.id!)?.count) {
				Modal.confirm({
					icon: <InfoCircleOutlined style={{ color: 'red' }} />,
					title: (
						<span className={styles.warningModalHeader}>
							{t('gameStatsPage.actions.deleteWarningModal.header')}
						</span>
					),
					okText: t('gameStatsPage.actions.deleteWarningModal.okButton'),
					cancelText: t(
						'gameStatsPage.actions.deleteWarningModal.cancelButton',
					),
					cancelButtonProps: {
						className: 'color-dark',
					},
					onCancel() {
						history.push(`leaderboards`);
					},
					content: (
						<>
							<div>
								{t('gameStatsPage.actions.deleteWarningModal.body.p1', {
									apiName: record.apiName,
								})}
							</div>
							<div>
								<ul>
									{getLeaderboard(record.id!)?.items?.map((item) => (
										<li
											key={item.id}
											style={{ listStyle: 'disc', listStylePosition: 'inside' }}
										>
											{item.apiName}
										</li>
									))}
								</ul>
							</div>
							<br />
							<div>
								{t('gameStatsPage.actions.deleteWarningModal.body.p2', {
									apiName: record.apiName,
								})}
							</div>
						</>
					),
					centered: true,
				});
			} else {
				Modal.confirm({
					icon: <InfoCircleOutlined style={{ color: 'red' }} />,
					title: (
						<span className={styles.warningModalHeader}>
							{t('gameStatsPage.actions.deleteModal.header')}
						</span>
					),
					okText: t('gameStatsPage.actions.deleteModal.okButton'),
					cancelText: t('common.cancel'),
					onOk() {
						return api.deleteGameStat(record.apiName).then((result) => {
							if (result) {
								OmniNotification.success({
									message: t('gameStatsPage.actions.deleteMessage'),
									description: t('gameStatsPage.actions.deleteDescription', {
										apiName: record.apiName,
									}),
								});
								handleTableChange(pagination);
							}
						});
					},
					cancelButtonProps: {
						className: 'color-dark',
					},
					content: (
						<span>
							{t('gameStatsPage.actions.deleteModal.body', {
								apiName: record.apiName,
							})}
						</span>
					),
					centered: true,
				});
			}
		},
		[leaderboardMap],
	);

	const getColumns = (t: TFunction, callbacks: any) => {
		const columns = [
			{
				title: t('gameStatsPage.columns.apiName'),
				key: 'apiName',
				render: (record: GameStatDto) => getCellValue(record?.apiName),
				ellipsis: true,
				width: '30%',
			},
			{
				title: t('gameStatsPage.columns.title'),
				key: 'title',
				render: (record: GameStatDto) => getCellValue(record?.title),
			},
			{
				title: t('gameStatsPage.columns.format'),
				key: 'format',
				render: (record: GameStatDto) =>
					getCellValue(t(TGameStatFormate[record?.format as GameStatFormat])),
				width: 250,
			},
			{
				title: t('gameStatsPage.columns.unitOfMeasurement'),
				key: 'unitOfMeasurement',
				render: (record: GameStatDto) =>
					getCellValue(
						t(
							TGameStatUnitOfMeasurement[
								record?.unitOfMeasurement as UnitOfMeasurement
							],
						),
					),
				width: 200,
			},
			{
				title: t('gameStatsPage.columns.actions'),
				key: 'actions',
				render: (record: GameStatDto) => (
					<div className={styles.ellipsisContainer}>
						<Dropdown
							overlay={getActionMenu(t, record, callbacks)}
							trigger={['click']}
							onVisibleChange={async (visible) => {
								if (visible) {
									syncLeaderboard(record);
								}
							}}
						>
							<EllipsisOutlined
								data-testid="test-action-btn"
								onClick={(e) => e.preventDefault()}
							/>
						</Dropdown>
					</div>
				),
				align: 'right' as 'right',
				width: 100,
			},
		];

		return columns;
	};

	const getActionMenu = (t: TFunction, record: GameStatDto, callbacks: any) => {
		return (
			<Menu>
				<Menu.Item
					key="edit"
					onClick={() => {
						callbacks.navigateToEditStat(record.id, record.apiName);
					}}
				>
					{t('gameStatsPage.actions.edit')}
				</Menu.Item>
				<Menu.Item
					key="delete"
					danger={true}
					onClick={() => callbacks.deleteStatsCallback(record)}
					data-testid="test-delete-btn"
					disabled={getLeaderboard(record.id!)?.isLoading}
				>
					{t('gameStatsPage.actions.delete')}
					<span style={{ marginLeft: 5 }}>
						{getLeaderboard(record.id!)?.isLoading ? (
							<LoadingOutlined style={{ color: '#262626' }} />
						) : (
							<InfoCircleOutlined
								style={{ opacity: getLeaderboard(record.id!)?.count ? 1 : 0, color: "red" }}
								/>
						)}
					</span>
				</Menu.Item>
			</Menu>
		);
	};

	const columns = useMemo(
		() =>
			getColumns(t, {
				deleteStatsCallback,
				navigateToEditStat,
			}),
		[leaderboardMap],
	);

	const handleTableChange = (tablePagination: TablePaginationConfig) => {
		setIsLoading(true);
		api
			.getGameStats(
				projectInfo?.productId as string,
				(tablePagination.current as number) - 1,
				tablePagination.pageSize as number,
			)
			.then((res) => {
				setIsLoading(false);
				setListData(res);
				setPagination({
					...pagination,
					...tablePagination,
					total: res.totalCount || 0,
				});
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	useEffect(() => {
		handleTableChange(pagination);
	}, []);

	const renderEmptyDataDescription = useMemo(() => {
		return (
			<div
				className={styles.emptyContainer}
				data-testid={'emptyDataDescription'}
			>
				<p>
					<span>{t('gameStatsPage.emptyDataDescription.line1')}</span>
					<br />
					<span>{t('gameStatsPage.emptyDataDescription.line2')}</span>
				</p>
			</div>
		);
	}, []);

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: 'game-services',
				breadcrumbName: t('gameServicesPage.header'),
			},
			{
				path: '',
				breadcrumbName: t('gameStatsPage.header'),
			},
		]);
	}, [projectInfo?.id, projectInfo?.name]);

	return (
		<>
			<div className={styles.pageContainer}>
				<Breadcrumb routes={routes} />
				<div className="page-head">
					<div>
						<h2 className="h2" data-testid="page-title">
							{t('gameStatsPage.header')}
						</h2>
					</div>
					<div className={styles.buttonContainer}>
						<Button
							type="primary"
							data-testid="create-game-stat-button"
							onClick={() => navigateToCreateStat()}
						>
							{t('gameStatsPage.create')}
						</Button>
					</div>
				</div>
				{!listData && <Spin />}
				{listData && listData.items?.length === 0 && renderEmptyDataDescription}
				{listData && (listData.items as any)?.length > 0 && (
					<Table
						className="table"
						size="middle"
						columns={columns}
						rowKey="apiName"
						onChange={handleTableChange}
						dataSource={listData.items}
						loading={isLoading}
						rowClassName={styles.tableRow}
						tableLayout="fixed"
						sortDirections={['descend', 'ascend', 'descend']}
						pagination={pagination}
					/>
				)}
			</div>
		</>
	);
}
